import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Phase 18: Public Onboarding - Device Linking
 * Step 3: Link device via QR code or manual entry
 */
function PublicOnboardingDevice() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('choice'); // 'choice', 'qr', 'manual'
  const [deviceId, setDeviceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanner, setScanner] = useState(null);

  useEffect(() => {
    // Check prerequisites
    const householdId = sessionStorage.getItem('onboarding_household_id');
    if (!householdId) {
      navigate('/family/onboarding/household');
    }

    // Cleanup scanner on unmount
    return () => {
      if (scanner) {
        scanner.stop().catch(() => {});
      }
    };
  }, [navigate, scanner]);

  const startQRScanner = async () => {
    setMode('qr');
    setError('');
    setScanning(true);

    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      setScanner(html5QrCode);

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          // QR Code gefunden
          setDeviceId(decodedText);
          html5QrCode.stop();
          setScanning(false);
          handleDeviceLink(decodedText);
        },
        (errorMessage) => {
          // Scanning error (ignore)
        }
      );
    } catch (err) {
      setError('Kamera-Zugriff verweigert. Bitte verwenden Sie die manuelle Eingabe.');
      setMode('manual');
      setScanning(false);
    }
  };

  const handleDeviceLink = async (deviceIdToLink) => {
    setLoading(true);
    setError('');

    try {
      const householdId = sessionStorage.getItem('onboarding_household_id');
      
      // First verify device
      await axios.get(`${API}/api/onboarding/device/verify/${deviceIdToLink}`);
      
      // Then link it
      await axios.post(`${API}/api/onboarding/device/link`, {
        household_id: householdId,
        device_id: deviceIdToLink
      });

      sessionStorage.setItem('onboarding_device_id', deviceIdToLink);
      navigate('/family/onboarding/senior');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Koppeln des Geräts');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    handleDeviceLink(deviceId);
  };

  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#22C55E] text-white flex items-center justify-center font-bold">✓</div>
            <div className="w-16 h-1 bg-[#22C55E]"></div>
            <div className="w-8 h-8 rounded-full bg-[#22C55E] text-white flex items-center justify-center font-bold">✓</div>
            <div className="w-16 h-1 bg-[#22C55E]"></div>
            <div className="w-8 h-8 rounded-full bg-[#6DCEE8] text-[#050816] flex items-center justify-center font-bold">3</div>
          </div>
          <p className="text-center text-sm text-neutral-400">Schritt 3 von 5</p>
        </div>

        {/* Card */}
        <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-neutral-600/40">
          <h2 className="text-3xl font-bold text-[#F9FAFB] mb-2">Gerät koppeln</h2>
          <p className="text-[#CBD5F5] mb-6">Verbinden Sie Ihr Hayat-Gerät</p>

          {/* Mode: Choice */}
          {mode === 'choice' && (
            <div className="space-y-4">
              <button
                onClick={startQRScanner}
                className="w-full py-5 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300"
              >
                📷 QR-Code scannen
              </button>
              <button
                onClick={() => setMode('manual')}
                className="w-full py-4 bg-neutral-800/50 text-[#CBD5F5] rounded-xl font-semibold text-base border border-neutral-600/40 hover:bg-neutral-800 transition-all"
              >
                ✏️ Geräte-ID manuell eingeben
              </button>
              <button
                onClick={() => navigate('/family/onboarding/senior')}
                className="w-full py-3 text-[#6DCEE8] rounded-xl font-semibold text-sm hover:underline"
              >
                ⏩ Überspringen (später koppeln)
              </button>
            </div>
          )}

          {/* Mode: QR Scanner */}
          {mode === 'qr' && (
            <div className="space-y-4">
              <div id="qr-reader" className="rounded-xl overflow-hidden"></div>
              {scanning && (
                <p className="text-center text-[#6DCEE8] animate-pulse">QR-Code wird gesucht...</p>
              )}
              <button
                onClick={() => {
                  if (scanner) scanner.stop();
                  setMode('choice');
                  setScanning(false);
                }}
                className="w-full py-3 bg-neutral-800/50 text-[#CBD5F5] rounded-xl font-semibold"
              >
                Zurück
              </button>
            </div>
          )}

          {/* Mode: Manual */}
          {mode === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#F9FAFB] mb-2">Geräte-ID</label>
                <input
                  type="text"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  placeholder="z.B. hayat-device-test-001"
                  required
                  className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] placeholder-neutral-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all font-mono text-sm"
                  disabled={loading}
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Die ID finden Sie auf der Rückseite Ihres Geräts
                </p>
              </div>

              {error && (
                <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] px-4 py-3 rounded-xl text-sm">
                  <span className="font-semibold">⚠️ {error}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Wird gekoppelt...' : 'Gerät koppeln'}
              </button>
              <button
                type="button"
                onClick={() => setMode('choice')}
                className="w-full py-3 bg-neutral-800/50 text-[#CBD5F5] rounded-xl font-semibold"
              >
                Zurück
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicOnboardingDevice;
